import type {
  BulkApproveFailureReason,
  BulkApproveResponse,
  OutstandingApprovalItem,
} from '@time-tracking/shared';
import { BULK_APPROVE_MAX_ITEMS } from '@time-tracking/shared';

import { isBulkApprovable } from './needsActionGrouping.js';
import type { ToastKind } from '../useToasts.js';

/**
 * Split a selection into requests the server will accept.
 *
 * `bulkApproveTimesheets` is a sequential per-item loop behind a 29-second
 * Lambda timeout, so the request is capped. One week's roster bounded it
 * implicitly before; a cross-week selection does not, and rejecting the whole
 * thing would leave the manager to deselect by hand.
 */
export function chunkForApproval(
  ids: readonly string[],
  max: number = BULK_APPROVE_MAX_ITEMS,
): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += max) {
    chunks.push(ids.slice(i, i + max));
  }
  return chunks;
}

/**
 * Fold per-chunk responses into one.
 *
 * Without this a chunked run reports only its last chunk, so a failure part-way
 * through would claim nothing was approved while sheets had already been.
 */
export function mergeBulkResults(results: readonly BulkApproveResponse[]): BulkApproveResponse {
  return {
    approved: results.flatMap((r) => r.approved),
    skipped: results.flatMap((r) => r.skipped),
    errors: results.flatMap((r) => r.errors),
  };
}

export interface BulkOutcome {
  message: string;
  kind: ToastKind;
  /** Failed rows stay selected and in place so the manager can see which. */
  keepSelection: boolean;
}

/**
 * What to say about a bulk approve.
 *
 * Three outcomes, deliberately distinct. The weekly inbox reports
 * `approved.length` unconditionally, which renders "Approved 0 timesheets" as a
 * success toast; over one week partial outcomes were rare, but across a year of
 * them — with co-approvers, recalls and closed periods in play — they are
 * routine. `skipped` is benign ("someone else got there first") and `errors` is
 * not, so they must not collapse into one number.
 */
export function describeBulkOutcome(
  result: BulkApproveResponse,
  requestedCount: number,
  weekCount: number,
): BulkOutcome {
  if (result.errors.length > 0) {
    // Name the self-approval rule rather than lumping it in with "couldn't be approved".
    // It is a segregation-of-duties rule, not a failure, and a manager who sees a bare
    // count next to their own still-submitted sheet has no way to learn why.
    //
    // And say what the rule actually is. R22 excludes the approver's own sheet from
    // every BULK path while leaving it individually approvable, so "left for someone
    // else to approve" overstates it — it sends the manager looking for a colleague to
    // do something they can do themselves, one row at a time.
    const selfRefused = result.errors.filter((e) => e.reason === 'self_approval').length;
    const others = result.errors.length - selfRefused;
    const parts = [`Approved ${result.approved.length} of ${requestedCount}.`];
    if (selfRefused > 0) {
      parts.push(
        selfRefused === 1
          ? 'Your own sheet was left out of the bulk action — approve it from its own row.'
          : 'Your own sheets were left out of the bulk action — approve them from their own rows.',
      );
    }
    if (others > 0) parts.push(`${others} couldn’t be approved.`);
    return { message: parts.join(' '), kind: 'error', keepSelection: true };
  }
  if (result.skipped.length > 0) {
    return {
      message: `Approved ${result.approved.length} of ${requestedCount}. ${result.skipped.length} ${
        result.skipped.length === 1 ? 'was' : 'were'
      } already handled by someone else.`,
      kind: 'warn',
      keepSelection: false,
    };
  }
  return {
    message: `Approved ${result.approved.length} ${
      result.approved.length === 1 ? 'sheet' : 'sheets'
    } across ${weekCount} ${weekCount === 1 ? 'week' : 'weeks'}.`,
    kind: 'success',
    keepSelection: false,
  };
}

/**
 * Drop from the selection anything the latest fetch says is no longer
 * approvable.
 *
 * Focus-refetch stays on so the list keeps telling the truth while a manager
 * works, which means rows can change underneath a selection: a co-approver
 * approves one, a contributor recalls another. A stale key inflates the bulk
 * button's count and then silently fails on submit.
 */
export function reconcileSelection(
  selected: ReadonlySet<string>,
  items: readonly OutstandingApprovalItem[],
): { next: Set<string>; dropped: number } {
  const stillSelectable = new Set(items.filter(isBulkApprovable).map((i) => i.key));
  const next = new Set<string>();
  for (const key of selected) {
    if (stillSelectable.has(key)) next.add(key);
  }
  return { next, dropped: selected.size - next.size };
}

/**
 * Where focus goes when the row holding it disappears.
 *
 * Only ever consulted for the row that currently holds focus or contains it — a
 * background refetch that removes some unrelated row must not move focus at all,
 * or a manager mid-sentence in the search box loses their place.
 */
export function nextFocusTarget(container: HTMLElement, removedRowKey: string): HTMLElement | null {
  const row = container.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(removedRowKey)}"]`);
  if (!row) return null;

  const sibling = row.nextElementSibling;
  const siblingButton = sibling?.querySelector<HTMLElement>('.row-name-btn');
  if (siblingButton) return siblingButton;

  // The group's own toggle, but ONLY if the group survives. When the approved row is
  // the last one in its body, that header disappears with it on the next refetch, so
  // focusing it would hand the caret to a node that is about to unmount — landing on
  // <body> anyway, which is the failure this function exists to prevent.
  const body = row.closest('tbody');
  const groupSurvives = (body?.querySelectorAll('[data-row-key]').length ?? 0) > 1;
  if (groupSurvives) {
    const groupToggle =
      body?.previousElementSibling?.querySelector<HTMLElement>('.act-group-head__toggle');
    if (groupToggle) return groupToggle;
  }

  return container.querySelector<HTMLElement>('.act-search input');
}

/**
 * What to tell a manager about one row that a bulk approve refused.
 *
 * R23 asks for the named R20 reason to be shown against the row, not folded into a
 * count. The toast could only ever say "3 couldn't be approved", which keeps three rows
 * selected and offers nothing to do about them — and the remedies differ completely: a
 * closed period is final, a permission problem needs an administrator, and a stale
 * request just needs retrying.
 *
 * A total map over the union rather than a lookup with a fallback, so adding a reason to
 * `BulkApproveFailureReason` fails the type check here instead of silently rendering
 * nothing against the row it describes.
 */
export const BULK_FAILURE_LABELS: Record<BulkApproveFailureReason, string> = {
  locked_period: 'Reporting period closed',
  forbidden: 'Not yours to approve',
  not_found: 'No longer exists',
  idempotency_conflict: 'Changed while sending — try again',
  self_approval: 'Your own sheet — approve it from its row',
  contended: 'Period was being closed — try again',
  unexpected: 'Couldn’t be approved',
};

/**
 * Map a bulk response's errors onto the row keys the table renders.
 *
 * The wire reports failures by `timesheetId`; rows are keyed by person and week. The
 * dispatched items are the only place both are known, so the join happens here rather
 * than being reconstructed later from a list that may already have refetched.
 */
export function failureReasonsByRowKey(
  result: BulkApproveResponse,
  dispatched: readonly OutstandingApprovalItem[],
): Record<string, BulkApproveFailureReason> {
  const keyByTimesheet = new Map(
    dispatched
      .filter((i): i is OutstandingApprovalItem & { timesheetId: string } => i.timesheetId !== null)
      .map((i) => [i.timesheetId, i.key]),
  );
  const out: Record<string, BulkApproveFailureReason> = {};
  for (const e of result.errors) {
    const key = keyByTimesheet.get(e.id);
    if (key !== undefined) out[key] = e.reason;
  }
  return out;
}
